import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Wrapper } from '../styles';
import { Box, Flex, LoadingPage } from '@/layout';
import { Button } from '@/components/UI/Button';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { theme } from '@/theme/Theme';
import { FormSelect } from '@/components/UI/Select';
import MultiSelect from '@/components/UI/Select/MultiSelect';
import { TextField } from '@mui/material';
import { submitGoGlobalApplication, refreshToken } from '@/api/auth.api';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import useCountryDetail from '@/hooks/useCountryDetail';
import { signIn } from '@/redux/auth/auth.slice';
import type { RootState } from '@/redux/store';
import { translatePblLawyerProfileText } from '@/api/pbl.api';
import { getAuthRefreshDeadline } from '@/utils/authToken';

interface FormStepperProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<string | null>>;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    refreshStatus?: () => Promise<void>;
    applicationData?: GoGlobalApplication | null;
}

interface Option {
    value: string;
    text: string;
};

const RegistrationForm = ({ setCurrentStep, setActiveStep, refreshStatus, applicationData }: FormStepperProps) => {
    // @ts-ignore
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [firmSizeOptions, setFirmSizeOptions] = useState<Option[]>([]);
    const [crossBorderExpertiseOptions, setCrossBorderExpertiseOptions] = useState<Option[]>([]);
    const [caseRecords, setCaseRecords] = useState<Array<{ en: string; zh: string }>>([{ en: '', zh: '' }]);
    const [caseRecordErrors, setCaseRecordErrors] = useState<boolean[]>([false]);

    // 从 Redux 获取当前的认证状态
    const authState = useSelector((state: RootState) => state.auth);
    // 使用 ref 保存最新的 authState，避免闭包问题
    const authStateRef = useRef(authState);
    // 保存 interval ID 用于清理
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // localStorage key for form data
    const STORAGE_KEY = 'goGlobalRegistrationFormData';
    const STORAGE_EXPIRY_DAYS = 7;
    const HEARTBEAT_INTERVAL = 30 * 60 * 1000; // 30分钟
    const INITIAL_DELAY = 10 * 1000; // 10秒初始延迟

    // 国家/地区的优先排序
    const countryPriorityOrder = [
        'China', 'Taiwan', 'Macao', 'Japan', 'South Korea', 'Singapore',
        'Malaysia', 'Thailand', 'Vietnam', 'Philippines', 'Indonesia', 'India', 'Myanmar',
        'Cambodia', 'Nepal', 'Australia', 'New Zealand', 'United Kingdom', 'Germany', 'France',
        'Spain', 'Italy', 'Portugal', 'Netherlands', 'Denmark', 'Sweden', 'Norway',
        'United States', 'Canada', 'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'South Africa', 'Egypt',
        'Kenya'
    ];

    // 语言的优先排序
    const languagePriorityOrder = [
        'English', 'Mandarin', 'Cantonese', 'Portuguese', 'Spanish', 'Arabic', 'Thai',
        'Nepali', 'Malay', 'Japanese', 'Korean', 'Latin', 'Vietnamese', 'Indonesian',
        'Burmese', 'Khmer', 'Hindi'
    ];

    // 排序函数
    const sortOptions = (
        options: { value: string | number; text: string | number }[],
        priorityOrder: string[]
    ) => {
        const sorted: { value: string | number; text: string | number }[] = [];
        const remaining: { value: string | number; text: string | number }[] = [];

        // 按优先顺序添加
        priorityOrder.forEach(priority => {
            const found = options.find(item => item.value === priority);
            if (found) {
                sorted.push(found);
            }
        });

        // 添加不在优先列表中的项目
        options.forEach(item => {
            if (!priorityOrder.includes(item.value as string)) {
                remaining.push(item);
            }
        });

        return [...sorted, ...remaining];
    };

    // 获取并排序国家选项（移除 Hong Kong SAR）
    const countryOptions = sortOptions(
        useCountryDetail('name').filter(item => item.value !== 'Hong Kong'),
        countryPriorityOrder
    );

    // 获取并排序语言选项
    const languageOptions = sortOptions(useCountryDetail('languages'), languagePriorityOrder);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<GoGlobalApplicationForm>();

    // 保存表单数据到 localStorage
    const saveToLocalStorage = (formData: any, caseRecordsData: Array<{ en: string; zh: string }>) => {
        try {
            const dataToSave = {
                formData,
                caseRecords: caseRecordsData,
                timestamp: new Date().getTime()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    };

    // 从 localStorage 恢复表单数据
    const loadFromLocalStorage = () => {
        try {
            const savedData = localStorage.getItem(STORAGE_KEY);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                const expiryTime = STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // 转换为毫秒
                const currentTime = new Date().getTime();

                // 检查数据是否过期
                if (currentTime - parsed.timestamp > expiryTime) {
                    localStorage.removeItem(STORAGE_KEY);
                    return null;
                }

                return parsed;
            }
        } catch (error) {
            console.error('Error loading from localStorage:', error);
        }
        return null;
    };

    // 清除 localStorage 数据
    const clearLocalStorage = () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    };

    // 保持 authStateRef 始终是最新的
    useEffect(() => {
        authStateRef.current = authState;
    }, [authState]);

    // Heartbeat 功能：定期刷新 token
    const performHeartbeat = useCallback(async () => {
        try {
            // 使用 ref 获取最新的 authState，避免依赖 authState 导致无限循环
            const currentAuth = authStateRef.current;
            if (currentAuth.accessToken && currentAuth.isLogin) {
                console.log('Heartbeat: 刷新 token...');
                const res = await refreshToken(currentAuth.accessToken);
                const newToken = res.data.data.access_token;

                // 更新 Redux store 中的 token
                dispatch(
                    signIn({
                        ...currentAuth,
                        isLogin: true,
                        accessToken: newToken,
                        refreshTokenTime: getAuthRefreshDeadline(res.data.data.expires_in)
                    })
                );

                console.log('Heartbeat: token 刷新成功');
            }
        } catch (error) {
            console.error('Heartbeat: token 刷新失败', error);
            // 如果 heartbeat 失败，不强制登出，让用户继续填写
            // 实际提交时会由 axios interceptor 处理认证问题
        }
    }, [dispatch]); // 只依赖 dispatch（dispatch 是稳定的引用）

    const prevHandler = () => {
        setCurrentStep('1');
        setActiveStep(0);
        setSearchParams({ step: '1' });
    };

    const nextHandler = () => {
        setCurrentStep('3');
        setActiveStep(2);
        setSearchParams({ step: '3' });
    };

    const addCaseRecord = () => {
        setCaseRecords([...caseRecords, { en: '', zh: '' }]);
        setCaseRecordErrors([...caseRecordErrors, false]);
    };

    const removeCaseRecord = (index: number) => {
        const newRecords = caseRecords.filter((_, i) => i !== index);
        const newErrors = caseRecordErrors.filter((_, i) => i !== index);
        setCaseRecords(newRecords);
        setCaseRecordErrors(newErrors);
    };

    const updateCaseRecord = (index: number, field: 'en' | 'zh', value: string) => {
        const newRecords = [...caseRecords];
        newRecords[index][field] = value;
        setCaseRecords(newRecords);

        // 清除该案例的错误状态（如果至少有一个字段有值）
        const newErrors = [...caseRecordErrors];
        newErrors[index] = false;
        setCaseRecordErrors(newErrors);
    };

    const onSubmit = handleSubmit(async (data) => {
        // 验证所有案例至少填写一个字段
        const newErrors = caseRecords.map(record =>
            record.en.trim() === '' && record.zh.trim() === ''
        );

        setCaseRecordErrors(newErrors);

        // 如果有任何案例为空，阻止提交
        if (newErrors.some(error => error)) {
            errorHandler(String((t as any)('error.caseRecordRequired', 'Please fill in at least one field (English or Chinese) for each case, or remove the empty case.')));
            return;
        }

        setIsLoading(true);

        try {
            // 过滤掉 en 和 zh 都为空的案例（虽然验证已经确保不会有空案例）
            const filteredCaseRecords = caseRecords.filter(record => record.en.trim() !== '' || record.zh.trim() !== '');

            const rawData: GoGlobalApplicationRequest = {
                ...data,
                languages: data.languages.join(', '),
                successful_case_records_data: filteredCaseRecords.length > 0 ? filteredCaseRecords : null,
            };
            const res = (await submitGoGlobalApplication(rawData)).data;
            if (res.success) {
                successHandler(t('success.saved'));
                // 清除 localStorage 中的暂存数据
                clearLocalStorage();
                // 重新获取最新的申请状态
                if (refreshStatus) {
                    await refreshStatus();
                }
                nextHandler();
            }
        } catch (error) {
            console.log(error);
            errorHandler(t('error.server'));
        } finally {
            setIsLoading(false);
        }
    });

    // 组件加载时恢复 localStorage 数据
    useEffect(() => {
        const savedData = loadFromLocalStorage();
        if (savedData) {
            // 恢复表单数据
            if (savedData.formData) {
                Object.keys(savedData.formData).forEach((key) => {
                    if (savedData.formData[key] !== undefined && savedData.formData[key] !== null) {
                        setValue(key as any, savedData.formData[key]);
                    }
                });
            }

            // 恢复案例记录
            if (savedData.caseRecords && savedData.caseRecords.length > 0) {
                setCaseRecords(savedData.caseRecords);
                setCaseRecordErrors(new Array(savedData.caseRecords.length).fill(false));
            }
        }
    }, [setValue]);

    // 监听表单数据变化并自动保存到 localStorage
    useEffect(() => {
        const subscription = watch((formData) => {
            saveToLocalStorage(formData, caseRecords);
        });
        return () => subscription.unsubscribe();
    }, [watch, caseRecords]);

    // 监听 caseRecords 变化并保存
    useEffect(() => {
        const formData = watch();
        saveToLocalStorage(formData, caseRecords);
    }, [caseRecords]);

    // Heartbeat: 定期刷新 token
    useEffect(() => {
        console.log('Heartbeat: 将在 10 秒后首次刷新 token，然后每 30 分钟刷新一次');

        // 10 秒后执行第一次 heartbeat
        const initialTimeout = setTimeout(() => {
            performHeartbeat();

            // 第一次执行后，启动定时器每 30 分钟执行一次
            heartbeatIntervalRef.current = setInterval(() => {
                performHeartbeat();
            }, HEARTBEAT_INTERVAL);
        }, INITIAL_DELAY);

        // 组件卸载时清除定时器
        return () => {
            clearTimeout(initialTimeout);
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
            console.log('Heartbeat: 定时器已清除');
        };
    }, [performHeartbeat]); // performHeartbeat 现在只依赖 dispatch（稳定），所以不会重新创建

    useEffect(() => {
        // 從多語系中取得 firmSize 選項
        const options = t('form.firmSize.options', { returnObjects: true }) as Option[];
        const firmSizeData = options.map((option) => ({
            value: option.value,
            text: option.text,
        }));
        setFirmSizeOptions(firmSizeData);

        // 從多語系中取得 crossBorderExpertise 選項
        const expertiseOptions = t('form.crossBorderExpertise.options', { returnObjects: true }) as Option[];
        const expertiseData = expertiseOptions.map((option) => ({
            value: option.value,
            text: option.text,
        }));
        setCrossBorderExpertiseOptions(expertiseData);
    }, [t]);


    const translateToTraditionalChinese = async (text: string) => {
        const response = await translatePblLawyerProfileText({
            text,
            from: 'en',
            to: 'zh-Hant',
        });

        return response.data.data.translated_text;
    };

    const translateCaseRecordToZh = async (id: number) => {
        const text = caseRecords[id]?.en?.trim();
        if (!text) return;

        try {
            const translatedText = await translateToTraditionalChinese(text);
            setCaseRecords((records) => {
                const newRecords = [...records];
                newRecords[id] = {
                    ...newRecords[id],
                    zh: translatedText,
                };
                return newRecords;
            });
        } catch (error) {
            console.error('Translation error:', error);
            errorHandler(t('error.translationFailed'));
        }
    };

    const translateCrossBorderExpertiseToZh = async () => {
        const fieldValue = watch('cross_border_expertise_other_en')?.trim();
        if (!fieldValue) return;

        try {
            const translatedText = await translateToTraditionalChinese(fieldValue);
            setValue('cross_border_expertise_other_zh', translatedText);
        } catch (error) {
            console.error('Translation error:', error);
            errorHandler(t('error.translationFailed'));
        }
    };

    return (
        <Wrapper>
            <LoadingPage isLoading={isLoading} />
            <Box>
                <form onSubmit={onSubmit}>
                    <InputWrapper style={{ height: 'auto' }}>
                        <FormSelect
                            control={control}
                            data={firmSizeOptions}
                            inputLabel={`${t('form.firmSize.label')} *`}
                            selectLabel={t('form.firmSize.placeholder')}
                            name={'firm_size'}
                            register={{ required: t('required.input', { input: t('form.firmSize.label') }) }}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <Controller
                            control={control}
                            name={'overseas_network_presence'}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <TextField
                                    label={`${t('form.overseasNetworkPresence.label')} *`}
                                    variant="standard"
                                    value={value || ''}
                                    sx={{ width: '100%' }}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    placeholder={t('form.overseasNetworkPresence.placeholder')}
                                    autoComplete="off"
                                />
                            )}
                            rules={{ required: t('required.input', { input: t('form.overseasNetworkPresence.label') }) }}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <MultiSelect
                            control={control}
                            data={countryOptions}
                            inputLabel={`${t('form.keyOverseasMarkets.label')} *`}
                            selectLabel={t('form.keyOverseasMarkets.placeholder')}
                            name={'key_overseas_markets'}
                            register={{ required: t('required.input', { input: t('form.keyOverseasMarkets.label') }) }}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <MultiSelect
                            control={control}
                            data={languageOptions}
                            inputLabel={`${t('form.goGlobalLanguages.label')} *`}
                            selectLabel={t('form.goGlobalLanguages.placeholder')}
                            name={'languages'}
                            register={{ required: t('required.input', { input: t('form.goGlobalLanguages.label') }) }}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <MultiSelect
                            control={control}
                            data={crossBorderExpertiseOptions}
                            inputLabel={`${t('form.crossBorderExpertise.label')} *`}
                            selectLabel={t('form.crossBorderExpertise.placeholder')}
                            name={'cross_border_expertise'}
                            register={{ required: t('required.input', { input: t('form.crossBorderExpertise.label') }) }}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <Controller
                            control={control}
                            name={'cross_border_expertise_other_en'}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <TextField
                                    label={String((t as any)('form.crossBorderExpertiseOtherEn.label', 'Cross-border Expertise Other (English)'))}
                                    variant="standard"
                                    value={value || ''}
                                    sx={{
                                        width: '100%',
                                        '& textarea': {
                                            resize: 'vertical'
                                        }
                                    }}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    placeholder={String((t as any)('form.crossBorderExpertiseOtherEn.placeholder', 'Please specify in English'))}
                                    autoComplete="off"
                                    multiline
                                    rows={8}
                                />
                            )}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                            <Button
                                type="button"
                                size="sm"
                                onClick={translateCrossBorderExpertiseToZh}
                            >
                                translate
                            </Button>
                        </div>
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <Controller
                            control={control}
                            name={'cross_border_expertise_other_zh'}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <TextField
                                    label={String((t as any)('form.crossBorderExpertiseOtherZh.label', '其他跨境專業 (中文)'))}
                                    variant="standard"
                                    value={value || ''}
                                    sx={{
                                        width: '100%',
                                        '& textarea': {
                                            resize: 'vertical'
                                        }
                                    }}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    placeholder={String((t as any)('form.crossBorderExpertiseOtherZh.placeholder', '請以中文填寫'))}
                                    autoComplete="off"
                                    multiline
                                    rows={8}
                                />
                            )}
                        />
                    </InputWrapper>

                    <InputWrapper style={{ height: 'auto' }}>
                        <Box style={{ width: '100%' }}>
                            <label style={{
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.6)',
                                marginBottom: '16px',
                                display: 'block'
                            }}>
                                {t('form.successfulCaseRecords.label')}
                            </label>

                            {caseRecords.map((record, index) => (
                                <Box key={index} style={{
                                    marginBottom: '24px',
                                    padding: '16px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    position: 'relative'
                                }}>
                                    <Flex justifyContent="space-between" alignItems="center" marginBottom={16}>
                                        <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                            {String((t as any)('form.successfulCaseRecords.caseLabel', 'Case'))} {index + 1}
                                        </label>
                                        {caseRecords.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="danger"
                                                size="sm"
                                                onClick={() => removeCaseRecord(index)}
                                                style={{
                                                    minWidth: 'auto',
                                                    padding: '4px 12px',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                {t('buttons.remove')}
                                            </Button>
                                        )}
                                    </Flex>

                                    <TextField
                                        label={'Case Description (English)'}
                                        variant="standard"
                                        value={record.en}
                                        sx={{
                                            width: '100%',
                                            '& textarea': {
                                                resize: 'vertical'
                                            }
                                        }}
                                        onChange={(e) => updateCaseRecord(index, 'en', e.target.value)}
                                        placeholder={'Enter case description in English'}
                                        autoComplete="off"
                                        multiline
                                        rows={6}
                                        error={caseRecordErrors[index]}
                                        helperText={caseRecordErrors[index] ? 'Please fill in at least one field (English or Chinese)' : null}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => translateCaseRecordToZh(index)}
                                        >
                                            translate
                                        </Button>
                                    </div>
                                    <TextField
                                        label={'案例描述 (中文)'}
                                        variant="standard"
                                        value={record.zh}
                                        sx={{
                                            width: '100%',
                                            '& textarea': {
                                                resize: 'vertical'
                                            }
                                        }}
                                        onChange={(e) => updateCaseRecord(index, 'zh', e.target.value)}
                                        placeholder={'請輸入中文案例描述'}
                                        autoComplete="off"
                                        multiline
                                        rows={6}
                                        error={caseRecordErrors[index]}
                                        helperText={caseRecordErrors[index] ? '請填寫至少一個欄位（英文或中文）' : null}

                                    />
                                </Box>
                            ))}

                            <Button
                                type="button"
                                variant="primary-invert"
                                size="md"
                                onClick={addCaseRecord}
                                style={{ marginTop: '8px' }}
                            >
                                + {t('buttons.addCase')}
                            </Button>
                        </Box>
                    </InputWrapper>

                    <Flex marginTop={50}>
                        <Button onClick={() => onSubmit()} size={'lg'} type="button" variant={'primary'} rounded={false} marginRight={40}>
                            {t('buttons.submit')}
                        </Button>
                        <Button size={'lg'} type="button" variant="primary-invert" marginRight={40} onClick={prevHandler}>
                            {t('buttons.back')}
                        </Button>
                    </Flex>
                </form>
            </Box>
        </Wrapper>
    );
};

export default RegistrationForm;
